/*
 *   File : manage-mail.js
 *   Author : https://evoqins.com
 *   Description : Manage mail file
 *   Integrations :NA
 *   Version : 1.0.0
*/

import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal } from 'bootstrap';

//Style
import style from "../../Style/Component/mail.module.scss";
import color from '../../Style/color.scss';

//Custom components
import { PrimaryButton, SecondaryButton, TextButton } from '../../Components/Buttons';
import { CreateUserGroup, SuccessModal, ViewTemplateModal } from '../../Components/Modal';
import { CustomEditor, CustomSelectBox, CustomTextInput, EmailQuillEditor, RichTextEditor } from '../../Components/FormElements';
import { CustomRadio } from '../../Components/Others';
import { toast } from 'react-toastify';
import { APIService } from '../../Service';
import { prepareQuillHtmlForEmail } from '../../Utils/quill-email';

const TARGET_USERS_DATA = [
    {
        label: 'All users',
        value: 4,
    },
    {
        label: 'Invested users',
        value: 1,
    },
    {
        label: 'Non invested users',
        value: 2
    },
    {
        label: 'Signed up users',
        value: 3
    }
];


const USER_SELECT_DATA = [
    {
        id: 1,
        label: "Predefined Template",
    },
    {
        id: 2,
        label: "Custom"
    }
];

const ManageMail = () => {

    const navigate = useNavigate();
    const { state } = useLocation();
    const hasFetchedRef = useRef(false);

    const [successModal, setSuccessModal] = useState(false);

    const [selectedUsers, setSelectedUsers] = useState(null);
    const [selectedUsersError, setSelectedUsersError] = useState("");
    const [selected, setSelected] = useState(1);

    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedTemplateError, setSelectedTemplateError] = useState("");

    const [emailSubject, setEmailSubject] = useState("");
    const [emailSubjectError, setEmailSubjectError] = useState("");
    const [content, setContent] = useState("");
    const [contentError, setContentError] = useState("");
    const [createGroupModal, setCreateGroupModal] = useState(false);

    const [createGroupSuccess, setCreateGroupSuccess] = useState(false);

    const [loader, setLoader] = useState(false);

    const [listLoader, setListLoader] = useState(false);
    const [templateList, setTemplateList] = useState([]);

    const [viewTemplateModal, setViewTemplateModal] = useState(false);

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    }, []);

    useEffect(() => {
        if (state !== null) {
            if (state.data) {
                setSelectedTemplate({
                    value: state.data.template_id,
                    label: state.data.title,
                    image: state.data.image,
                    title: state.data.title,
                    content: state.data.content,
                    html: state.data.html
                })
            }

            if (state.edit === true) {
                setSelected(1);
            }
        } else {
            setSelectedTemplate(null)
        }
    }, [state]);

    useEffect(() => {
        if (successModal === true) {
            const modal = new Modal(document.getElementById("success-modal"));
            modal.show();
        }
    }, [successModal]);

    useEffect(() => {
        if (viewTemplateModal === true) {
            const modal = new Modal(document.getElementById("view-template-modal"));
            modal.show();
        }
    }, [viewTemplateModal]);

    useEffect(() => {
        if (createGroupModal === true) {
            const modal = new Modal(document.getElementById("create-group-modal"));
            modal.show();
        }
    }, [createGroupModal]);

    useEffect(() => {
        if (createGroupSuccess === true) {
            const modal = new Modal(document.getElementById("success-modal"));
            modal.show();
        }
    }, [createGroupSuccess]);

    useEffect(() => {
        if (hasFetchedRef.current) return;

        hasFetchedRef.current = true;
        _templateList();
        _listGroup();
    }, []);


    useEffect(() => {
        setSelectedUsersError("");
    }, [selectedUsers]);

    useEffect(() => {
        setSelectedTemplateError("");
    }, [selectedTemplate, selected]);

    useEffect(() => {
        setEmailSubjectError("");
    }, [emailSubject, selected]);

    useEffect(() => {
        setContentError("");
    }, [content, selected]);

    // handle back 
    const _handleBack = () => {
        navigate("/send-mail", {
            state: {
                tab_index: 2
            }
        });
    };

    // handle view template 
    const _handleView = () => {
        setViewTemplateModal(true);
    };

    // handle back 
    const _handleCancel = () => {
        navigate("/send-mail", {
            state: {
                tab_index: 1
            }
        });
    };

    // handle select user 
    const _handleSelect = (id) => {
        setSelected(id);
    };

    const _handleChange = (value) => {
        setContent(value)
    };

    // handle create group 
    const _handleCreateGroup = () => {
        setCreateGroupModal(true);
    };

    // handle continue to create group 
    const _handleContinue = (name) => {
        navigate("/customers", {
            state: {
                tab_index: 1,
                type: 'invested',
                group_name: name,
                path: "/send-mail/manage-mail"
            }
        });
    };

    // handle send mail 
    const _handleSendMail = () => {
        let is_valid = true;

        if (selectedUsers === null) {
            setSelectedUsersError("Please select target users");
            is_valid = false;
        }

        if (selected === 1) {

            if (selectedTemplate === null && state?.edit === false) {
                setSelectedTemplateError("Please select template");
                is_valid = false;
            }

        } else {
            if (emailSubject.trim().length === 0) {
                setEmailSubjectError("Please provide email subject");
                is_valid = false;
            }

            if (content.length === 0) {
                setContentError("Please provide content");
                is_valid = false;
            }
        }

        if (is_valid === true) {

            _sendEmail();
        }
    };

    // API - send email
    const _sendEmail = () => {
        let is_post = true;
        let url = 'send-email/send';
        let request = {
            "user_type": selectedUsers.value,
            "template_id": selected === 1 ? selectedTemplate.value : null,

            "template_type": selected,
            "subject": selected === 1 ? null : emailSubject,
            "content": selected === 1 ? null : prepareQuillHtmlForEmail(content)
        }
        setLoader(true);
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                setSuccessModal(true);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setLoader(false);
        });
    };

    // API - Template lis
    const _templateList = () => {
        let is_post = true;
        let url = 'send-email/list-templates';
        let request = {
            "page_num": 1,
            "page_size": 20
        }
        // setLoader(true);
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {

                let updated_list = response.data.data.map((item) => ({
                    value: item.template_id,
                    label: item.title,
                    image: item.image,
                    title: item.title,
                    content: item.content,
                    html: item.html
                }))

                setTemplateList(updated_list);
            } else {
                setTemplateList([]);
            }
            setListLoader(false);
        });
    };

    const [userGroupLoader, setUserGroupLoader] = useState(true);
    const [groupList, setGroupList] = useState([]);

    // API - Get group list
    const _listGroup = () => {
        setUserGroupLoader(true);
        let is_post = true;
        let url = 'user/user-groups';
        let request = {
            "page_num": 1,
        }
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                let updated_list = response.data.data.map(data => ({
                    value: data.id,
                    label: data.name
                }))
                console.log("updated_list", updated_list);

                setGroupList(updated_list);
            } else {
                setGroupList([]);
            }
            setUserGroupLoader(false);
        })
    };

    return (
        <Fragment>
            <section className='e-section-height pb-5' >
                <div className='e-bg-lavender padding-52px-lr pt-2 padding-112px-bottom'>
                    <p className='mb-0 e-text-chinese-black e-primary-font e-font-16 e-font-weight-400 line-height-24px'>
                        <span className='text-decoration-hover-underline cursor-pointer'
                            onClick={() => _handleCancel()}>Send Mail</span> / <span className='e-font-weight-600'>
                            {state?.edit === true ? "Send template" : "Send mail"}
                        </span>
                    </p>
                </div>

                <div className='padding-52px-lr margin--80px-top'>
                    <div className={`${style.e_wrapper} e-bg-white border-radius-16 padding-32px-all`}>
                        <h4 className='mb-4 e-text-smoky-black e-primary-font e-font-16 e-font-weight-600 line-height-20px'>
                            {state?.edit === true ? "Send template" : "Send mail"}
                        </h4>
                        <div className='row'>
                            <div className='col-9'>
                                <CustomSelectBox title='Target users'
                                    placeholder='Select user group'
                                    controlWidth='w-100'
                                    required={true}
                                    height='2.5rem'
                                    options={groupList}
                                    isSearchable={true}
                                    loader={userGroupLoader}
                                    borderColor={`0.031rem solid ${color.arsenic}`}
                                    value={selectedUsers}
                                    padding="6px 1rem"
                                    error={selectedUsersError}
                                    onChange={(e) => setSelectedUsers(e)} />
                            </div>

                            {state?.edit === true ? null :
                                <div className='col-3 my-auto'>
                                    <div className='mt-4'>
                                        <TextButton name="Create user groups"
                                            add={true}
                                            onClick={_handleCreateGroup} />
                                    </div>
                                </div>
                            }
                        </div>

                        <div className='row'>
                            <div className='col-9'>
                                {/* Custom radio button */}
                                {state?.edit === true ? null :
                                    <div className='mt-4'>
                                        <CustomRadio label="Choose type"
                                            required={true}
                                            data={USER_SELECT_DATA}
                                            selected={selected}
                                            className="d-flex align-items-center gap-3"
                                            handleSelect={_handleSelect} />
                                    </div>
                                }

                                <div className='mt-4'>
                                    {selected == 1 ?
                                        <div className={state?.edit === true ? "pe-none opacity-50" : ""}>
                                            <CustomSelectBox title='Select template'
                                                placeholder='Select template'
                                                controlWidth='w-100'
                                                required={true}
                                                height='2.5rem'
                                                options={templateList}
                                                loader={listLoader}
                                                isSearchable={true}
                                                borderColor={`0.031rem solid ${color.arsenic}`}
                                                value={selectedTemplate}
                                                padding="6px 1rem"
                                                error={selectedTemplateError}
                                                onChange={(e) => {
                                                    if (state?.edit === false || state === null) {
                                                        setSelectedTemplate(e);
                                                    }
                                                }} />
                                        </div>

                                        :
                                        // Custom 
                                        <>
                                            <CustomTextInput label="Email subject"
                                                placeholder="Enter subject"
                                                type="text"
                                                required={true}
                                                value={emailSubject}
                                                error={emailSubjectError}
                                                className="w-100 "
                                                inputType={2}
                                                handleChange={(e) => setEmailSubject(e)} />

                                            <div className='mt-4'>
                                                {/* <CustomEditor label="Email content"
                                                    required={true}
                                                    editorState={content}
                                                    error={contentError}
                                                    onEditorStateChange={_handleChange} /> */}

                                                {/* <RichTextEditor label="Email content"
                                                    value={content}
                                                    required={true}
                                                    error={contentError}
                                                    onEditorStateChange={_handleChange} /> */}

                                                <EmailQuillEditor label="Email content"
                                                    value={content}
                                                    required={true}
                                                    error={contentError}
                                                    onEditorStateChange={_handleChange} />
                                            </div>
                                        </>
                                    }
                                </div>

                                <div className={`${selected === 1 ? "justify-content-between" : "justify-content-end"} d-flex align-items-center gap-3 pt-4 mt-2 `}>
                                    {selected === 1 &&
                                        <>
                                            {selectedTemplate === null ?
                                                <div className={selectedTemplate === null ? "pe-none opacity-75" : ""}>
                                                    <TextButton name="View" />
                                                </div>
                                                :
                                                <TextButton name="View"
                                                    onClick={() => _handleView()} />
                                            }
                                        </>
                                    }
                                    <div className='d-flex align-items-center gap-3 '>
                                        <SecondaryButton name='Cancel'
                                            className="e-text-smoky-black"
                                            paddingBlock='padding-12px-tb padding-52px-lr'
                                            onClick={_handleCancel} />
                                        <PrimaryButton name='Send mail'
                                            paddingBlock='padding-12px-tb padding-40px-lr'
                                            loader={loader}
                                            className={loader ? 'w-139px' : ""}
                                            onClick={_handleSendMail} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {successModal === true &&
                <SuccessModal title="Success!"
                    description={<>Sending email to <span className='e-font-weight-600'>{selectedUsers.label}</span> has been successfully completed.</>}
                    handleSuccess={_handleBack} />
            }

            {createGroupModal === true &&
                <CreateUserGroup groupType={1}
                    handleClose={() => {
                        setTimeout(() => {
                            setCreateGroupModal(false);
                        }, 200);
                    }}
                    handleContinue={_handleContinue}
                    handleSuccess={() => {
                        setTimeout(() => {
                            setCreateGroupModal(false);
                            setCreateGroupSuccess(true);
                        }, 200);
                    }}
                />
            }

            {createGroupSuccess === true &&
                <SuccessModal title="Success!"
                    description="Group created successfully. Customers have been added to the group."
                    handleSuccess={() => {
                        setTimeout(() => {
                            setCreateGroupSuccess(false);
                        }, 200);
                        _listGroup();

                        // navigate("/customers", {
                        //     state: {
                        //         tab_index: 2,
                        //         type: 'user_group'
                        //     }
                        // });
                    }} />
            }

            {viewTemplateModal === true &&
                <ViewTemplateModal data={selectedTemplate}
                    handleClose={() => {
                        setTimeout(() => {
                            setViewTemplateModal(false);
                        }, 200);
                    }} />
            }
        </Fragment>
    )
}

export default ManageMail;
